import { useEffect, useState } from "react";
import { useSaveSettingsMutation, useSettingsQuery } from "@/lib/queries";
import { ApiError } from "@/lib/api";
import type { UserSettings } from "@/types";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * ERIKA's *My Settings* drawer.
 *
 * Three controls there, five here. The two extra — the default topic and
 * audience filters — are the ones people actually asked for: a researcher who
 * only ever reads external documents was re-selecting that on every question.
 *
 * ERIKA saved on every change with no Save button. This has one, because the
 * two select boxes there were instantaneous and a multi-select filter is not:
 * clicking through six topics would be six writes, and a half-applied set left
 * behind by a dropped request is worse than an unsaved form.
 */
const FORMATS: { value: UserSettings["outputFormat"]; label: string }[] = [
  { value: "summary", label: "Summary" },
  { value: "bullets", label: "Bullet points" },
  { value: "email", label: "Email-ready" },
];

const VOICES: { value: UserSettings["outputVoice"]; label: string }[] = [
  { value: "formal", label: "Formal" },
  { value: "casual", label: "Casual" },
  { value: "email", label: "Email-ready" },
];

export function MySettings({ onClose }: { onClose: () => void }) {
  const query = useSettingsQuery();
  const save = useSaveSettingsMutation();
  const [draft, setDraft] = useState<UserSettings | null>(null);

  // Seeded from the server rather than initialised to the defaults, so a
  // drawer opened before the fetch lands does not show "summary/formal" and
  // then flip to what the person actually chose.
  useEffect(() => {
    if (query.data) setDraft(query.data);
  }, [query.data]);

  const dirty =
    draft !== null && query.data !== undefined && JSON.stringify(draft) !== JSON.stringify(query.data);

  return (
    <>
      {/* Clicking away closes it. A drawer that can only be dismissed by its
          own button is a modal wearing a drawer's clothes. */}
      <button
        type="button"
        aria-label="Close settings"
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
      />
      <aside
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col border-l border-border bg-card shadow-xl"
        role="dialog"
        aria-label="My settings"
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="erika-serif text-lg">My settings</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {query.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {query.isError && (
            <p className="text-sm text-muted-foreground">Your settings could not be loaded.</p>
          )}

          {draft && (
            <>
              <label className="flex items-start gap-2.5">
                <Checkbox
                  className="mt-0.5"
                  checked={draft.advancedOpen}
                  onCheckedChange={(c) => setDraft({ ...draft, advancedOpen: c === true })}
                />
                <span>
                  <span className="block text-sm">Advanced options open by default</span>
                  <span className="block text-xs text-muted-foreground">
                    The Ask page starts with the filters expanded.
                  </span>
                </span>
              </label>

              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="settings-format">
                  Default output format
                </label>
                <Select
                  id="settings-format"
                  value={draft.outputFormat}
                  onChange={(e) =>
                    setDraft({ ...draft, outputFormat: e.target.value as UserSettings["outputFormat"] })
                  }
                >
                  {FORMATS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="settings-voice">
                  Default output voice
                </label>
                <Select
                  id="settings-voice"
                  value={draft.outputVoice}
                  onChange={(e) =>
                    setDraft({ ...draft, outputVoice: e.target.value as UserSettings["outputVoice"] })
                  }
                >
                  {VOICES.map((v) => (
                    <option key={v.value} value={v.value}>
                      {v.label}
                    </option>
                  ))}
                </Select>
                {/* Said plainly rather than left to be discovered: these two
                    are recorded on the question and are what the voice and
                    format prompt selectors will read once the gateway can
                    list prompts (PRD D1/D2). Until then they are a stored
                    preference and nothing else, and claiming otherwise would
                    be the kind of half-true a settings screen should not tell. */}
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Saved with each question you ask. Prompt selection itself is not yet
                  offered — see the prompt catalogue decision in the PRD.
                </p>
              </div>

              <div className="space-y-2 border-t border-border pt-4">
                <label className="flex items-start gap-2.5">
                  <Checkbox
                    className="mt-0.5"
                    checked={draft.defaultNoYear}
                    onCheckedChange={(c) => setDraft({ ...draft, defaultNoYear: c === true })}
                  />
                  <span className="text-sm">Include documents with no year, by default</span>
                </label>
                <label className="flex items-start gap-2.5">
                  <Checkbox
                    className="mt-0.5"
                    checked={draft.defaultNoTags}
                    onCheckedChange={(c) => setDraft({ ...draft, defaultNoTags: c === true })}
                  />
                  <span className="text-sm">Include documents with no topic, by default</span>
                </label>
              </div>
            </>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-border px-5 py-4">
          <span className="text-xs text-muted-foreground">
            {save.isError
              ? save.error instanceof ApiError
                ? save.error.message
                : "Those settings could not be saved."
              : save.isSuccess && !dirty
                ? "Saved."
                : ""}
          </span>
          <Button
            variant="primary"
            size="sm"
            disabled={!draft || !dirty || save.isPending}
            onClick={() => draft && save.mutate(draft)}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </footer>
      </aside>
    </>
  );
}
